<template>
  <div class="cad-wrapper">
    <div class="toolbar">
      <button @click="addRect">Adicionar Retângulo</button>
      <button @click="addPoly">Adicionar Polígono (triângulo)</button>
      <button :disabled="!selected" @click="addAdjacent('left')">Adicionar Adjacente Esquerda</button>
      <button :disabled="!selected" @click="addAdjacent('right')">Adicionar Adjacente Direita</button>
      <button :disabled="!selected" @click="addAdjacent('top')">Adicionar Adjacente Topo</button>
      <button :disabled="!selected" @click="addAdjacent('bottom')">Adicionar Adjacente Inferior</button>
      <button @click="exportJSON">Exportar JSON</button>
      <span class="info">Objetos: {{ shapes.length }}</span>
    </div>

    <div class="stage" ref="stageWrapper">
      <svg
        ref="svg"
        :width="width"
        :height="height"
        @mousedown="onStageMouseDown"
        @mousemove="onStageMouseMove"
        @mouseup="onStageMouseUp"
        @mouseleave="onStageMouseUp"
      >
        <rect :width="width" :height="height" fill="#fafafa" />

        <!-- grid optional -->
        <g v-if="showGrid">
          <line v-for="i in Math.ceil(width/20)" :key="`v-${i}`" :x1="i*20" :y1="0" :x2="i*20" :y2="height" stroke="#eee"/>
          <line v-for="i in Math.ceil(height/20)" :key="`h-${i}`" :x1="0" :y1="i*20" :x2="width" :y2="i*20" stroke="#eee"/>
        </g>

        <!-- shapes -->
        <g v-for="shape in shapes" :key="shape.id">
          <template v-if="shape.type==='rect'">
            <rect
              :x="shape.x"
              :y="shape.y"
              :width="shape.w"
              :height="shape.h"
              :fill="shape.fill"
              :stroke="shape.stroke"
              :stroke-width="shape.strokeWidth"
              @mousedown.stop.prevent="selectShape(shape, $event)"
              :opacity="selected && selected.id===shape.id ? 0.9 : 1"
            />

            <!-- extremidades (cantos) -->
            <circle v-for="p in rectCorners(shape)" :key="p.x+','+p.y" :cx="p.x" :cy="p.y" r="4" fill="white" stroke="#333" stroke-width="1"/>

            <!-- resize handles when selected -->
            <g v-if="selected && selected.id===shape.id">
              <rect v-for="handle in rectResizeHandles(shape)" :key="handle.name" :x="handle.x-6" :y="handle.y-6" width="12" height="12" fill="#fff" stroke="#333" stroke-width="1"
                @mousedown.stop.prevent="startResize(shape, handle.name, $event)"
              />
            </g>
          </template>

          <template v-else-if="shape.type==='poly'">
            <polygon :points="polyPointsString(shape.points)" :fill="shape.fill" :stroke="shape.stroke" :stroke-width="shape.strokeWidth"
              @mousedown.stop.prevent="selectShape(shape, $event)"
            />

            <!-- extremidades (todos os vértices) -->
            <circle v-for="(p, idx) in shape.points" :key="idx" :cx="p.x" :cy="p.y" r="4" fill="white" stroke="#333" stroke-width="1"
              @mousedown.stop.prevent="startDragPoint(shape, idx, $event)"
            />
          </template>
        </g>

        <!-- selection bbox -->
        <rect v-if="selected" :x="selBBox.x" :y="selBBox.y" :width="selBBox.w" :height="selBBox.h" fill="none" stroke="#1976d2" stroke-dasharray="4 3"/>
      </svg>
    </div>
  </div>
</template>

<script>
// Vue 2 single file component (Options API)
export default {
  name: 'MarmoCad',
  props: {
    width: { type: Number, default: 1200 },
    height: { type: Number, default: 700 },
    showGrid: { type: Boolean, default: true }
  },
  data() {
    return {
      shapes: [],
      nextId: 1,
      selected: null,
      dragging: false,
      dragStart: null,
      resize: null, // {shape, handle, start}
      dragPoint: null // {shape, idx}
    }
  },
  computed: {
    selBBox() {
      if (!this.selected) return { x:0,y:0,w:0,h:0 }
      if (this.selected.type === 'rect') {
        return { x: this.selected.x, y: this.selected.y, w: this.selected.w, h: this.selected.h }
      } else {
        const xs = this.selected.points.map(p=>p.x)
        const ys = this.selected.points.map(p=>p.y)
        const x = Math.min(...xs), y = Math.min(...ys)
        return { x, y, w: Math.max(...xs)-x, h: Math.max(...ys)-y }
      }
    }
  },
  methods: {
    // --- creation ---
    addRect() {
      const w = 200, h = 100
      const x = 50 + (this.shapes.length * 20) % (this.width - w)
      const y = 50 + (this.shapes.length * 12) % (this.height - h)
      const shape = { id: this.nextId++, type: 'rect', x, y, w, h, stroke: '#333', fill: '#ddd', strokeWidth: 1 }
      this.shapes.push(shape)
      this.selectShape(shape)
    },
    addPoly() {
      const baseX = 200 + (this.shapes.length*15) % 400
      const baseY = 120 + (this.shapes.length*10) % 300
      const shape = {
        id: this.nextId++,
        type: 'poly',
        points: [ {x: baseX, y: baseY}, {x: baseX+140, y: baseY}, {x: baseX+70, y: baseY+90} ],
        stroke: '#333', fill: '#e8f0ff', strokeWidth: 1
      }
      this.shapes.push(shape)
      this.selectShape(shape)
    },

    // --- selection & dragging ---
    selectShape(shape, event) {
      this.selected = shape
      // prepare drag
      this.dragging = true
      const pt = this.getMousePos(event)
      this.dragStart = { x: pt.x, y: pt.y, shapeX: shape.x, shapeY: shape.y }
      // for polygon store individual offsets
      if (shape.type === 'poly') {
        this.dragStart.points = shape.points.map(p=>({x: p.x, y: p.y}))
      }
    },
    onStageMouseDown(e) {
      // click on empty area deselects
      if (e.target === this.$refs.svg) {
        this.selected = null
      }
    },
    onStageMouseMove(e) {
      if (this.resize) {
        this.doResize(e)
        return
      }
      if (this.dragPoint) {
        this.doDragPoint(e)
        return
      }
      if (!this.dragging || !this.selected) return
      const pt = this.getMousePos(e)
      const dx = pt.x - this.dragStart.x
      const dy = pt.y - this.dragStart.y
      if (this.selected.type === 'rect') {
        this.selected.x = this.dragStart.shapeX + dx
        this.selected.y = this.dragStart.shapeY + dy
      } else {
        // move poly by adding delta to each point
        this.selected.points.forEach((p, idx) => {
          p.x = this.dragStart.points[idx].x + dx
          p.y = this.dragStart.points[idx].y + dy
        })
      }
    },
    onStageMouseUp() {
      this.dragging = false
      this.dragStart = null
      this.resize = null
      this.dragPoint = null
    },

    // --- resize (rect only) ---
    rectCorners(shape) {
      return [
        { x: shape.x, y: shape.y },
        { x: shape.x + shape.w, y: shape.y },
        { x: shape.x + shape.w, y: shape.y + shape.h },
        { x: shape.x, y: shape.y + shape.h }
      ]
    },
    rectResizeHandles(shape) {
      const c = this.rectCorners(shape)
      return [
        { name: 'nw', x: c[0].x, y: c[0].y },
        { name: 'ne', x: c[1].x, y: c[1].y },
        { name: 'se', x: c[2].x, y: c[2].y },
        { name: 'sw', x: c[3].x, y: c[3].y }
      ]
    },
    startResize(shape, handle, event) {
      this.resize = { shape, handle, start: this.getMousePos(event), orig: Object.assign({}, shape) }
    },
    doResize(event) {
      if (!this.resize) return
      const pt = this.getMousePos(event)
      const dx = pt.x - this.resize.start.x
      const dy = pt.y - this.resize.start.y
      const s = this.resize.shape
      const o = this.resize.orig
      if (this.resize.handle === 'nw') {
        s.x = o.x + dx
        s.y = o.y + dy
        s.w = o.w - dx
        s.h = o.h - dy
      } else if (this.resize.handle === 'ne') {
        s.y = o.y + dy
        s.w = o.w + dx
        s.h = o.h - dy
      } else if (this.resize.handle === 'se') {
        s.w = o.w + dx
        s.h = o.h + dy
      } else if (this.resize.handle === 'sw') {
        s.x = o.x + dx
        s.w = o.w - dx
        s.h = o.h + dy
      }
      // normalize min sizes
      if (s.w < 10) s.w = 10
      if (s.h < 10) s.h = 10
    },

    // --- polygon editing ---
    polyPointsString(points) {
      return points.map(p => `${p.x},${p.y}`).join(' ')
    },
    startDragPoint(shape, idx, event) {
      this.dragPoint = { shape, idx, start: this.getMousePos(event), orig: { x: shape.points[idx].x, y: shape.points[idx].y } }
    },
    doDragPoint(event) {
      if (!this.dragPoint) return
      const pt = this.getMousePos(event)
      const dx = pt.x - this.dragPoint.start.x
      const dy = pt.y - this.dragPoint.start.y
      const p = this.dragPoint.shape.points[this.dragPoint.idx]
      p.x = this.dragPoint.orig.x + dx
      p.y = this.dragPoint.orig.y + dy
    },

    // --- adjacency (add piece at side) ---
    addAdjacent(side) {
      if (!this.selected) return
      const s = this.selected
      if (s.type === 'rect') {
        let newRect = null
        if (side === 'left') {
          newRect = { id: this.nextId++, type: 'rect', x: s.x - s.w, y: s.y, w: s.w, h: s.h, stroke: '#333', fill: '#f5f5f5', strokeWidth: 1 }
        } else if (side === 'right') {
          newRect = { id: this.nextId++, type: 'rect', x: s.x + s.w, y: s.y, w: s.w, h: s.h, stroke: '#333', fill: '#f5f5f5', strokeWidth: 1 }
        } else if (side === 'top') {
          newRect = { id: this.nextId++, type: 'rect', x: s.x, y: s.y - s.h, w: s.w, h: s.h, stroke: '#333', fill: '#f5f5f5', strokeWidth: 1 }
        } else if (side === 'bottom') {
          newRect = { id: this.nextId++, type: 'rect', x: s.x, y: s.y + s.h, w: s.w, h: s.h, stroke: '#333', fill: '#f5f5f5', strokeWidth: 1 }
        }
        if (newRect) {
          this.shapes.push(newRect)
          this.selectShape(newRect)
        }
      } else {
        // For polygon we attach by bounding box same logic
        const bbox = this.getPolyBBox(s)
        let newRect = null
        if (side === 'left') newRect = { id: this.nextId++, type: 'rect', x: bbox.x - bbox.w, y: bbox.y, w: bbox.w, h: bbox.h, stroke: '#333', fill: '#f2f2f2', strokeWidth: 1 }
        if (side === 'right') newRect = { id: this.nextId++, type: 'rect', x: bbox.x + bbox.w, y: bbox.y, w: bbox.w, h: bbox.h, stroke: '#333', fill: '#f2f2f2', strokeWidth: 1 }
        if (side === 'top') newRect = { id: this.nextId++, type: 'rect', x: bbox.x, y: bbox.y - bbox.h, w: bbox.w, h: bbox.h, stroke: '#333', fill: '#f2f2f2', strokeWidth: 1 }
        if (side === 'bottom') newRect = { id: this.nextId++, type: 'rect', x: bbox.x, y: bbox.y + bbox.h, w: bbox.w, h: bbox.h, stroke: '#333', fill: '#f2f2f2', strokeWidth: 1 }
        if (newRect) {
          this.shapes.push(newRect)
          this.selectShape(newRect)
        }
      }
    },
    getPolyBBox(poly) {
      const xs = poly.points.map(p=>p.x)
      const ys = poly.points.map(p=>p.y)
      const x = Math.min(...xs), y = Math.min(...ys)
      const w = Math.max(...xs)-x, h = Math.max(...ys)-y
      return { x, y, w, h }
    },

    // --- utilities ---
    getMousePos(evt) {
      const svg = this.$refs.svg
      const rect = svg.getBoundingClientRect()
      const cx = evt.clientX - rect.left
      const cy = evt.clientY - rect.top
      return { x: cx, y: cy }
    },

    // --- export ---
    exportJSON() {
      // create a clean serializable structure
      const data = { width: this.width, height: this.height, shapes: this.shapes.map(s => {
        if (s.type === 'rect') return { id: s.id, type: s.type, x: s.x, y: s.y, w: s.w, h: s.h }
        return { id: s.id, type: s.type, points: s.points.map(p => ({ x: p.x, y: p.y })) }
      }) }
      const json = JSON.stringify(data, null, 2)
      // for convenience, copy to clipboard if available
      if (navigator && navigator.clipboard) navigator.clipboard.writeText(json).catch(()=>{})
      // emit an event so parent can catch
      this.$emit('export', data)
      // also show in console
      console.log('Export JSON:', data)
      alert('JSON copiado para área de transferência (se suportado) e emitido como evento `export`. Veja console.')
      return data
    }
  }
}
</script>

<style scoped>
.cad-wrapper{ display:flex; flex-direction:column; font-family: Arial, Helvetica, sans-serif }
.toolbar{ padding:8px; background:#fff; display:flex; gap:8px; align-items:center; border-bottom:1px solid #eee }
.stage{ padding:12px; background:#f8f8f8 }
svg{ background:linear-gradient(180deg,#fff,#fafafa); box-shadow:0 2px 8px rgba(0,0,0,0.04) }
button{ padding:6px 10px; border-radius:6px; border:1px solid #ccc; background:#fff; cursor:pointer }
button:disabled{ opacity:0.5; cursor:not-allowed }
.info{ margin-left:12px; color:#666 }
</style>
